import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileDown, Tv } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getTournament, getRounds } from '@/lib/api';
import { publicReportUrl } from '@/lib/appSurface';
import { showErrorToast } from '@/lib/toast';
import type { Tournament, Round } from '@swiss-magic/shared-types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function PrintingCenterPage() {
  const { t } = useTranslation('director');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    Promise.all([getTournament(id), getRounds(id)])
      .then(([t, r]) => {
        setTournament(t);
        setRounds(r);
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !tournament) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t('printing.loading')}</p>
      </div>
    );
  }

  const publishedRounds = rounds.filter(
    (r) => r.status === 'published' || r.status === 'completed',
  );

  const canPrintStartingList = [
    'registration_closed',
    'in_progress',
    'completed',
  ].includes(tournament.status);

  const canPrintStandings = publishedRounds.length > 0;

  async function downloadPdf(path: string, key: string) {
    setGenerating(key);
    try {
      // The public-page path matches the PDF path segment, so the same
      // string builds the QR target the backend embeds in the PDF.
      const publicUrl = publicReportUrl(tournament!.slug, path);
      const res = await fetch(
        `${API_URL}/t/${tournament!.slug}/pdf/${path}?publicUrl=${encodeURIComponent(publicUrl)}`,
      );
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers.get('content-disposition')
        ?.match(/filename="(.+)"/)?.[1] ?? 'report.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showErrorToast(t('printing.generate_failed_toast'));
    } finally {
      setGenerating(null);
    }
  }

  return (
    <div data-testid="printing-center-page" className="w-full">
      <main className="mx-auto max-w-4xl">
        <h2 className="mb-6 text-2xl font-semibold">
          {t('printing.title_suffix', { name: tournament.name })}
        </h2>

        <div className="space-y-8">
          {/* PDF Downloads */}
          <section>
            <h3 className="mb-4 text-lg font-medium">{t('printing.pdf_section_title')}</h3>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.starting_list_label')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canPrintStartingList || generating === 'starting-list'}
                  onClick={() => downloadPdf('starting-list', 'starting-list')}
                >
                  <FileDown className="mr-1 h-4 w-4" />
                  {generating === 'starting-list'
                    ? t('printing.generating')
                    : t('printing.download_pdf')}
                </Button>
                {!canPrintStartingList && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('printing.starting_list_unavailable')}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.standings_label')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canPrintStandings || generating === 'standings'}
                  onClick={() => downloadPdf('standings', 'standings')}
                >
                  <FileDown className="mr-1 h-4 w-4" />
                  {generating === 'standings'
                    ? t('printing.generating')
                    : t('printing.download_pdf')}
                </Button>
                {!canPrintStandings && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('printing.standings_unavailable')}
                  </p>
                )}
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.pairings_label')}</p>
                {publishedRounds.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('printing.no_published_rounds')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {publishedRounds.map((round) => (
                      <Button
                        key={round.id}
                        variant="outline"
                        size="sm"
                        disabled={generating === `pairings-${round.roundNumber}`}
                        onClick={() =>
                          downloadPdf(
                            `pairings/${round.roundNumber}`,
                            `pairings-${round.roundNumber}`,
                          )
                        }
                      >
                        <FileDown className="mr-1 h-4 w-4" />
                        {generating === `pairings-${round.roundNumber}`
                          ? t('printing.round_generating')
                          : t('printing.round_button', { roundNumber: round.roundNumber })}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* TV Display */}
          <section>
            <h3 className="mb-4 text-lg font-medium">{t('printing.tv_section_title')}</h3>
            <p className="mb-3 text-sm text-muted-foreground">
              {t('printing.tv_section_description')}
            </p>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.starting_list_label')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/t/${tournament.slug}/tv/starting-list`, '_blank')}
                >
                  <Tv className="mr-1 h-4 w-4" />
                  {t('printing.open_button')}
                </Button>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.standings_label')}</p>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!canPrintStandings}
                  onClick={() => window.open(`/t/${tournament.slug}/tv/standings`, '_blank')}
                >
                  <Tv className="mr-1 h-4 w-4" />
                  {t('printing.open_button')}
                </Button>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-muted-foreground">{t('printing.pairings_label')}</p>
                {publishedRounds.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('printing.no_published_rounds')}</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {publishedRounds.map((round) => (
                      <Button
                        key={round.id}
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(
                            `/t/${tournament.slug}/tv/pairings/${round.roundNumber}`,
                            '_blank',
                          )
                        }
                      >
                        <Tv className="mr-1 h-4 w-4" />
                        {t('printing.round_button', { roundNumber: round.roundNumber })}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
